import { addMonths, format, parse, startOfQuarter, subMonths, subQuarters } from 'date-fns';

import { getQuarterLabel } from '@/utils/getQuarterLabel';

import type { EstateAnalysis } from './analytics';

type StatisticsType = {
  count: number;
  mean: number;
  percentile: number;
  daysOnMarket: number;
};

type ValueChange = {
  value: number;
  changePercent?: number;
};

// PriceHubble argument
export type DealType = 'sale' | 'rent_gross';

const getMonthsOfLastXQuarters = (quarters: number) => {
  const startOldestQuarter = startOfQuarter(subQuarters(new Date(), quarters - 1));
  const months = [];
  for (let i = 0; i < quarters * 3; i++) {
    const date = addMonths(startOldestQuarter, i);
    months.push(format(date, 'yyyy-MM'));
  }
  return months;
};

const getMonths = (months: number) =>
  Array.from({ length: months }, (_, i) => {
    const date = subMonths(new Date(), i);
    return format(date, 'yyyy-MM');
  });

const MONTHS_FILTER: Record<number, string[]> = {
  12: getMonths(12),
  24: getMonthsOfLastXQuarters(8),
  48: getMonths(48),
};

const getStatistics = (
  offerStatistics: EstateAnalysis['offerStatistics'],
  metric: string,
  months: number
) => {
  const today = new Date();
  const currentMonth = format(today, 'yyyy-MM');

  const dataSource =
    offerStatistics.find((dt) => {
      return dt.data.items[0].statistics.find((statistic) => statistic.metric === metric);
    })?.data?.items || [];

  const currentMonthIndex = dataSource.findIndex((item) => item.groupId === currentMonth);

  if (currentMonthIndex === -1) {
    // If the current month is not found in the data source, return immediately
    return undefined;
  }

  const data = dataSource.filter(({ groupId }) => MONTHS_FILTER[months].includes(groupId));

  const timeRange = `${parse(dataSource[0].groupId, 'yyyy-MM', new Date()).getFullYear()} - ${parse(dataSource[dataSource.length - 1].groupId, 'yyyy-MM', new Date()).getFullYear()}`;

  const groupedData = data.reduce<Record<string, StatisticsType>>((acc, item) => {
    const itemDate = parse(item.groupId, 'yyyy-MM', new Date());
    const label =
      months === 12
        ? format(itemDate, "MMM''yy")
        : months === 24
          ? getQuarterLabel(itemDate)
          : itemDate.getFullYear().toString();

    const statistics: StatisticsType = {
      count: item.statistics.find((el) => el.type === 'count' && el.metric === metric)?.value || 0,
      mean: item.statistics.find((el) => el.type === 'mean' && el.metric === metric)?.value || 0,
      percentile:
        item.statistics.find((el) => el.type === 'percentile' && el.metric === metric)?.value || 0,
      daysOnMarket:
        item.statistics.find((el) => el.type === 'mean' && el.metric === 'days_on_market')?.value ||
        0,
    };
    if (!acc[label]) {
      acc[label] = statistics;
    } else {
      acc[label] = {
        count: (acc[label].count + statistics.count) / 2,
        mean: (acc[label].mean + statistics.mean) / 2,
        percentile: (acc[label].percentile + statistics.percentile) / 2,
        daysOnMarket: (acc[label].daysOnMarket + statistics.daysOnMarket) / 2,
      };
    }
    return acc;
  }, {});
  const statisticsEntries = Object.entries(groupedData);
  const statisticsData = statisticsEntries.map<
    StatisticsType & { label: string; prevPeriod?: { changePercent: number } }
  >(([key, value], index) => {
    const prevValues = index > 0 ? statisticsEntries[index - 1][1] : undefined;
    return {
      label: key,
      count: Math.round(value.count),
      mean: Math.round(value.mean),
      percentile: Math.round(value.percentile),
      daysOnMarket: Math.round(value.daysOnMarket),
      prevPeriod:
        typeof prevValues?.mean !== 'number' || prevValues.mean === 0 || value.mean === 0
          ? undefined
          : {
              changePercent: ((value.mean - prevValues.mean) * 100) / prevValues.mean,
            },
    };
  });

  // Average Sales/Rent Price
  const currentMonthMeanValue =
    dataSource[currentMonthIndex].statistics.find(
      (el) => el.type === 'mean' && el.metric === metric
    )?.value || 0;
  let avgPrice: ValueChange | undefined;

  if (dataSource[currentMonthIndex - months]) {
    const targetMonthMeanValue = dataSource[currentMonthIndex - months].statistics.find(
      (el) => el.type === 'mean' && el.metric === metric
    )?.value;
    if (typeof targetMonthMeanValue === 'number') {
      avgPrice = {
        value: currentMonthMeanValue,
        changePercent: targetMonthMeanValue
          ? ((currentMonthMeanValue - targetMonthMeanValue) * 100) / targetMonthMeanValue
          : undefined,
      };
    }
  }

  // Last month's median price
  let lastMonthMedianPrice: ValueChange | undefined;

  if (dataSource[currentMonthIndex - 1]) {
    const currentMonthMedianPriceValue =
      dataSource[currentMonthIndex].statistics.find(
        (el) => el.type === 'percentile' && el.metric === metric
      )?.value || 0;

    const lastMonthMedianPriceValue = dataSource[currentMonthIndex - 1].statistics.find(
      (el) => el.type === 'percentile' && el.metric === metric
    )?.value;

    if (typeof lastMonthMedianPriceValue === 'number') {
      lastMonthMedianPrice = {
        value: lastMonthMedianPriceValue,
        changePercent: lastMonthMedianPriceValue
          ? ((currentMonthMedianPriceValue - lastMonthMedianPriceValue) * 100) /
            lastMonthMedianPriceValue
          : undefined,
      };
    }
  }

  // Last 4 years % change
  const prev4YMeanValue =
    dataSource[0]?.statistics.find((el) => el.type === 'mean' && el.metric === metric)?.value || 0;
  const last4YPercentChange =
    prev4YMeanValue !== 0
      ? ((currentMonthMeanValue - prev4YMeanValue) * 100) / prev4YMeanValue
      : undefined;

  return {
    timeRange,
    statisticsData,
    lastMonthMedianPrice,
    last4YPercentChange,
    avgPrice,
  };
};

export const useOfferStatistics = (
  estateAnalysis: EstateAnalysis,
  price: 'price' | 'price_per_square_meter',
  months: number
): {
  data: Record<
    DealType,
    | {
        timeRange: string;
        statisticsData: (StatisticsType & {
          label: string;
          prevPeriod?: { changePercent: number };
        })[];
        lastMonthMedianPrice?: ValueChange;
        last4YPercentChange?: number;
        avgPrice?: ValueChange;
      }
    | undefined
  >;
} => {
  return {
    data: {
      sale: getStatistics(
        estateAnalysis.offerStatistics,
        price === 'price' ? 'sale_price' : 'sale_price_per_square_meter',
        months
      ),
      rent_gross: getStatistics(
        estateAnalysis.offerStatistics,
        price === 'price' ? 'rent_gross' : 'rent_gross_per_square_meter',
        months
      ),
    },
  };
};
